import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const PARTICLES = 64;
const LIFETIME_MS = 1800;
const TONES = ['accent', 'accent-2', 'accent-3', 'success', 'warning'];

export function celebrate(element) {
  const rect = element?.getBoundingClientRect?.();
  const origin = rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } : null;
  window.dispatchEvent(new CustomEvent('celebrate', { detail: origin }));
}

function createBurst(origin) {
  const lift = origin && origin.y < window.innerHeight / 3 ? 50 : -60;
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    x: origin?.x ?? window.innerWidth / 2,
    y: origin?.y ?? window.innerHeight * 0.4,
    particles: Array.from({ length: PARTICLES }, (_, index) => {
      const angle = (index / PARTICLES) * Math.PI * 2 + Math.random() * 0.5;
      const distance = 90 + Math.random() * 190;
      return {
        dx: Math.cos(angle) * distance,
        dy: Math.sin(angle) * distance * 0.8 + lift,
        rotate: Math.round(Math.random() * 720 - 360),
        size: 5 + Math.round(Math.random() * 6),
        delay: Math.round(Math.random() * 120),
        tone: TONES[index % TONES.length],
        round: index % 3 === 0,
      };
    }),
  };
}

export function Celebration() {
  const [bursts, setBursts] = useState([]);

  useEffect(() => {
    const timers = new Set();
    const onCelebrate = (event) => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const burst = createBurst(event.detail);
      setBursts((current) => [...current, burst]);
      const timer = setTimeout(() => {
        timers.delete(timer);
        setBursts((current) => current.filter((item) => item.id !== burst.id));
      }, LIFETIME_MS);
      timers.add(timer);
    };
    window.addEventListener('celebrate', onCelebrate);
    return () => {
      window.removeEventListener('celebrate', onCelebrate);
      timers.forEach(clearTimeout);
    };
  }, []);

  if (bursts.length === 0) return null;

  return createPortal(
    <div className="celebration" aria-hidden="true">
      {bursts.map((burst) => (
        <div key={burst.id} className="celebration__burst" style={{ left: burst.x, top: burst.y }}>
          <span className="celebration__ring" />
          <span className="celebration__ring celebration__ring--late" />
          {burst.particles.map((particle, index) => (
            <span
              key={index}
              className={`celebration__piece celebration__piece--${particle.tone}${particle.round ? ' is-round' : ''}`}
              style={{
                '--dx': `${particle.dx}px`,
                '--dy': `${particle.dy}px`,
                '--rotate': `${particle.rotate}deg`,
                '--size': `${particle.size}px`,
                '--delay': `${particle.delay}ms`,
              }}
            />
          ))}
        </div>
      ))}
    </div>,
    document.body
  );
}
